import {
    ApplicationType,
    BoolArrayFilter,
    Module,
    ModuleName,
    moduleNames,
    modules,
    getModuleLabel,
    NavDropdown,
    NewsRoutes,
    AgrometeorologyRoutes,
    PlantProtectionRoutes,
    CommunicationRoutes,
    RoleType,
} from "modules/common";
import { useGetUserNotifications } from "modules/communication";
import { createModuleUsageLogData } from "modules/dashboard/store/moduleActivity/thunks";
import React, { useCallback, useMemo, useContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import { NavLink, useHistory } from "react-router-dom";
import { AppRoutes } from "routing";
import { CreateModuleUsageLogRequest } from "services/logger";
import { RootState } from "store";
import { AccessibilityContext } from "views/AccessibilityContext";

const NavItem = React.forwardRef<
    HTMLAnchorElement,
    { module: Module; roleType: RoleType; hasUnreadNotifications?: boolean }
>(({ module, roleType, hasUnreadNotifications }, ref) => {
    const className = `nav__item ${module.disabledForRelease ? "nav__item--disabled" : ""}`;
    const dispatch = useDispatch();
    const history = useHistory();

    const handleCreateModuleUsageLog = (e: React.MouseEvent) => {
        const moduleValue = Object.values(moduleNames).find(
            moduleName => moduleName.label === module.label
        )?.value;
        
        if (!moduleValue) return;

        const createModuleUsageLogRequest: CreateModuleUsageLogRequest = {
            moduleUsageType: moduleValue,
            applicationSourceType: ApplicationType.Web,
        };

        dispatch(createModuleUsageLogData(createModuleUsageLogRequest));
    };

    const moduleDefaultRoutes: Record<string, string> = {
        [AppRoutes.News]: NewsRoutes.News,
        [AppRoutes.Agrometeorology]: AgrometeorologyRoutes.WeatherToday,
        [AppRoutes.PlantProtection]: PlantProtectionRoutes.PestAnalysis,
        [AppRoutes.Communication]: CommunicationRoutes.SendNotification,
    };

    const handleClick = (e: React.MouseEvent) => {
        handleCreateModuleUsageLog(e);
        
        const defaultRoute = moduleDefaultRoutes[module.route!];
        if (defaultRoute) {
            e.preventDefault();
            history.push(defaultRoute);
        }
    };

    const getNavLinkTo = () => {
        return moduleDefaultRoutes[module.route!] ?? module.route ?? "#";
    };

    return module.route ? (
        <NavLink
            key={module.id}
            className={className}
            to={getNavLinkTo()}
            onClick={handleClick}
            innerRef={ref}
            isActive={(match, location) => {
                if (match) return true;
                return location.pathname.startsWith(module.route!);
            }}
        >
            <span className="nav__label-wrapper">
                {hasUnreadNotifications && (
                    <span className="nav__unread-dot" aria-hidden="true" />
                )}
                <span className="nav__label">{getModuleLabel(module.id, roleType)}</span>
            </span>
        </NavLink>
    ) : (
        <a
            key={module.id}
            href={module.externalUrl}
            className={className}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleCreateModuleUsageLog}
            ref={ref}
        >
            <span className="nav__label-wrapper">
                {hasUnreadNotifications && (
                    <span className="nav__unread-dot" aria-hidden="true" />
                )}
                <span className="nav__label">{getModuleLabel(module.id, roleType)}</span>
            </span>
        </a>
    );
});

export const Navigation: React.FC = () => {
    const { profile } = useSelector((state: RootState) => state.profile);
    const roleType = profile?.roleType?.value ?? RoleType.None;
    const { lowVision } = useContext(AccessibilityContext);

    const isModuleAccessible = useCallback(
        (module: Module) =>
            (!Boolean(roleType) && module.roles.includes(RoleType.None)) ||
            (module.roles.includes(roleType) && profile?.moduleName.includes(module.id)),
        [profile?.moduleName, roleType]
    );

    const filteredModules = useMemo(() => {
        const allModules = modules(lowVision ?? false);
        return allModules.filter(isModuleAccessible);
    }, [isModuleAccessible, lowVision]);

    const hasUserNotificationsModule = filteredModules.some(
        m => m.id === ModuleName.UserNotifications
    );
    const { data: unreadNotificationsData } = useGetUserNotifications(
        {
            search: "",
            isHighlighted: false,
            source: [],
            isRead: [BoolArrayFilter.False],
            page: 1,
            pageSize: 1,
        },
        hasUserNotificationsModule
    );
    const hasUnreadNotifications =
        (unreadNotificationsData?.userNotificationsCount ?? 0) > 0;

    return (
        <nav role="navigation" className="nav__list">
            {filteredModules.map(module => {
                if (module.children) {
                    const accessibleChildren = module.children.filter(isModuleAccessible);
                    if (accessibleChildren.length === 0) return null;

                    return (
                        <NavDropdown key={module.id} name={getModuleLabel(module.id, roleType)} label={getModuleLabel(module.id, roleType)}>
                            {accessibleChildren.map(subModule => (
                                <NavItem
                                    key={subModule.id}
                                    module={subModule}
                                    roleType={roleType}
                                    hasUnreadNotifications={
                                        subModule.id === ModuleName.UserNotifications &&
                                        hasUnreadNotifications
                                    }
                                />
                            ))}
                        </NavDropdown>
                    );
                }

                return (
                    <NavItem
                        key={module.id}
                        module={module}
                        roleType={roleType}
                        hasUnreadNotifications={
                            module.id === ModuleName.UserNotifications && hasUnreadNotifications
                        }
                    />
                );
            })}
        </nav>
    );
};
